/* eslint-disable @typescript-eslint/default-param-last */
import _ from 'underscore';
import Promise from 'bluebird';
import Globalize, { DateFormatterOptions, NumberFormatterOptions, UnitFormatterOptions } from 'globalize';

import { LocaleManager } from './locale';
import { Resource } from './resource';
import { SignOption, IDateOptions, IDateFormats, II18nDataPromise, ICivilDate, ICivilDateTime, ICache } from './types';

export * from './types';

const localeManager = new LocaleManager();

const dynamicLocaleInstances = [];

/**
 * @this I18nModel
 * @param {String} key localization key
 * @returns {void}
 * @private
 */
function assertReady(this: I18nModel, key?: string): void {
  if (!this.isReady()) {
    const debugInfo = `name-${this.name},culture-${this.locale},key-${key}`;

    throw new Error(`The resource is not yet ready(${debugInfo}). Please either call sync APIs only when the promise is fulfilled, or switch to use async APIs.`);
  }
}

/**
 * @this I18nModel
 * @param {String} key localization key
 * @returns {Resource} Current synchronous resource
 * @private
 */
function synchronousResource(this: I18nModel, key: string): Resource {
  assertReady.call(this, key);
  return this.p$data.value().resource;
}

/**
 * @this I18nModel
 * @returns {Globalize} Current synchronous globalize
 * @private
 */
function synchronousGlobalize(this: I18nModel): Globalize {
  assertReady.call(this);
  return this.p$data.value().globalize;
}

/**
 * If the given object is thenable
 * @param {any} object - The object to be tested
 * @returns {boolean}
 * @private
 */
function isThenable(object: any): boolean {
  return object && _.isFunction(object.then);
}

/**
 * @this I18nModel
 * @param {string} locale - the locale used to load data
 * @returns {Promise} Promise of translation resource and globalize
 * @private
 */
function loadData(this: I18nModel, locale: string): II18nDataPromise {
  const translationData = this.loadTranslationData(locale);
  const cldrData = this.loadCLDRData(locale);

  if (isThenable(translationData) || isThenable(cldrData)) {
    return Promise.props({
      locale,
      resource: Promise.resolve(translationData).then(data => new Resource({ data, failFast: this.failFast })),
      globalize: Promise.resolve(cldrData).then(cldr => new Globalize(cldr)),
    });
  }

  return Promise.resolve({
    locale,
    resource: new Resource({
      data: translationData as Record<string, string>,
      failFast: this.failFast,
    }),
    globalize: new Globalize(cldrData),
  });
}

/* @this I18nModel */
function dateMethodOptions(this: I18nModel, {
  skeleton,
  date,
  time,
  datetime,
  raw,
  timeZone = this.timeZone,
} : IDateOptions = {}): IDateOptions {
  return _.pick({
    skeleton,
    date,
    time,
    datetime,
    raw,
    timeZone,
  }, Boolean);
}

/* @this I18nModel */
function withSignWrapper(this: I18nModel, withSign: SignOption, method: (value: number) => string): (value: number) => string {
  switch (withSign) {
    case 'auto':
      return value => method(value);
    case 'never':
      return value => method(Math.abs(value));
    case 'always': {
      const cldr = this.getCldr();
      const minusSign = cldr.main('numbers/symbols-numberSystem-latn/minusSign');
      const plusSign = cldr.main('numbers/symbols-numberSystem-latn/plusSign');

      return (value: number) => {
        if (value < 0) {
          return method(value);
        }
        // Same as described in CLDR ticket
        // see: http://unicode.org/cldr/trac/ticket/8732
        // Note: 0 will have no sign
        return method(-value).replace(
          // only support latn for now
          minusSign,
          plusSign,
        );
      };
    }
    default:
      throw new RangeError(`'withSign' option should be one of 'auto', 'never' or 'always', but being ${withSign}`);
  }
}

/**
 * Return a function synchronously format a number with given options.
 * @param {string} [withSign=auto] - Show positive/negative sign or not.
 *    Could be 'always', 'auto' or 'never'. 'auto' means show sign for negative but not positive.
 * @param {Object} [options] - Same options as Globalize.js number formatter
 * @return {Function} The function formatting number
 * @private
 */
function numberFormatterWithSign(this: I18nModel, withSign: SignOption, options: Record<string, any>): (value: number) => string {
  const globalizeFormatter = synchronousGlobalize.call(this).numberFormatter(options);
  // SignificantDigits related param override IntegerDigits/FractionDigits related param
  // pick Boolean to remove them if they're not passed in
  return withSignWrapper.call(this, withSign, value => globalizeFormatter(value));
}

/**
 * Return a function synchronously format a currency with given options.
 * @param {string} currency - currency code
 * @param {string} [withSign=auto] - Show positive/negative sign or not.
 *    Could be 'always', 'auto' or 'never'. 'auto' means show sign for negative but not positive.
 * @param {Object} [options] - Options for currency formatter
 * @return {Function} The function formatting currency
 * @private
 */
function currencyFormatterWithSign(this: I18nModel, currency: string, withSign: SignOption = 'auto', options: Record<string, any>): (value: number) => string {
  if (!currency) {
    return () => '0';
  }

  const globalizeFormatter = synchronousGlobalize.call(this).currencyFormatter(currency, options);
  return withSignWrapper.call(this, withSign, value => globalizeFormatter(value));
}

function currencyFormatterWithSymbol(this: I18nModel, currency: string, withSign: SignOption = 'auto', withSymbol: boolean = true, options: Record<string, any>): (value: number) => string {
  if (!currency) {
    return () => '0';
  }

  if (withSymbol) {
    return currencyFormatterWithSign.call(this, currency, withSign, options);
  }

  // @ts-ignore
  const globalizeFormatter = synchronousGlobalize.call(this).currencyToPartsFormatter(currency, options);
  const formatter = (value: number): string => {
    const parts = globalizeFormatter(value);
    const newParts = _.map(parts, (part: { type: string; value: string; }) => {
      if (part.type === 'integer'
        || part.type === 'group'
        || part.type === 'decimal'
        || part.type === 'fraction'
      ) {
        return part.value;
      }
      return '';
    });
    const finalValue = newParts.join('');

    return finalValue;
  };

  return withSignWrapper.call(this, withSign, formatter);
}

function withGlobalizeCache(fnName: string, i18nModel: I18nModel): (...args: any[]) => any {
  const realMethod = i18nModel[fnName] as Function;

  function createCache(): ICache {
    const cache: ICache = {} as ICache;
    cache.size = 0;
    return cache;
  }

  return function wrappedGlobalizeMethod(this: any, ...args: any[]): any {
    const cache = i18nModel.parserFormatterCache;
    if (!cache[fnName]) {
      cache[fnName] = createCache();
    }

    let specificMethodCache: ICache = cache[fnName];

    const key = JSON.stringify(args);
    if (specificMethodCache[key]) {
      return specificMethodCache[key];
    }

    /* Just a precaution, don't expect cache to grow this large */
    if (specificMethodCache.size > 100) {
      cache[fnName] = createCache();
      specificMethodCache = cache[fnName];
    }

    specificMethodCache[key] = realMethod.apply(this, args);
    specificMethodCache.size += 1;

    return specificMethodCache[key];
  };
}

/**
 * I18n Model.
 */
export class I18nModel {
  static defaultFailFast: boolean | string = false;

  name?: string;

  locale: string;

  timeZone: string;

  currency: string;

  loadCLDRData: (locale: string) => any;

  loadTranslationData: (locale: string) => Promise<{ [key: string]: string }> | { [key: string]: string };

  missingKeys: { [key: string]: boolean };

  failFast: boolean | string;

  parserFormatterCache: { [key: string]: ICache };

  p$data: II18nDataPromise;

  /**
   * Set global locale used for all i18n model as default locale.
   * @param {string} locale - should be in format of IETF language tag.
   * @returns {Promise<I18nModel[]>} resolved when all dynamic locale i18n models are ready
   */
  static setGlobalLocale(locale: string): Promise<I18nModel[]> {
    localeManager.setGlobalLocale(locale);
    return Promise.all(_.map(dynamicLocaleInstances, instance => instance.setLocale(locale)));
  }

  /**
   * Load CLDR data for Globalize.js.
   * @param {JSON[]} data - CLDR data.
   * @returns {void}
   */
  static loadCLDRData(...data: any[]): void {
    Globalize.load(...[data]);
  }

  /**
   * Load time zone data for Globalize.js.
   * @param {JSON} data - Time zone data.
   * @returns {void}
   */
  static loadTimeZoneData(data: any): void {
    Globalize.loadTimeZone(data);
  }

  /**
   * Sets the default failfast value for every i18nmodel
   * @param {string|boolean} failfast when key missing
   * @returns {void}
   */
  static setDefaultFailFast(failfast: string | boolean): void {
    I18nModel.defaultFailFast = failfast;
  }

  /**
   * Create a model.
   * @param {Object} options - The initialization options
   * @param {string} [options.name] - The name of the i18n package
   * @param {string} [options.locale=current] - The locale of this i18n model
   * @param {string} [options.timeZone] - The default timeZone used by formatDate
   * @param {string} [options.currency] - The default currency used by formatCurrency
   * @param {function} options.loadCLDRData - a function which accepts locale as param and returns CLDR data
   * @param {function} options.loadTranslationData - a function which accepts locale as param and returns translation data
   * @param {string|boolean} options.failFast - when key missing
   * @constructs I18nModel
   */
  constructor({
    name,
    locale = 'current',
    timeZone,
    currency,
    loadCLDRData,
    loadTranslationData,
    failFast = I18nModel.defaultFailFast,
  }: {
    name?: string;
    locale?: string;
    timeZone: string;
    currency: string;
    loadCLDRData: (locale: string) => any;
    loadTranslationData: (locale: string) => Promise<{ [key: string]: string }> | { [key: string]: string };
    failFast?: string | boolean;
  }) {
    this.locale = locale;
    this.name = name;
    this.timeZone = timeZone;
    this.currency = currency;

    this.loadCLDRData = loadCLDRData;
    this.loadTranslationData = loadTranslationData;
    this.missingKeys = {};
    this.failFast = failFast;
    this.parserFormatterCache = {};

    /* These methods return parsers and formatters that can be safely cached. */
    [
      'decimalParser',
      'percentParser',
      'dateFormatter',
      'dateToPartsFormatter',
      'dateParser',
    ].forEach((fnName: string) => {
      this[fnName] = withGlobalizeCache(fnName, this);
    });

    if (locale === 'dynamic') {
      dynamicLocaleInstances.push(this);
    }

    if (locale === 'current' || locale === 'dynamic') {
      const p$globalLocale = localeManager.p$globalLocale();

      if (p$globalLocale.isFulfilled()) {
        this.p$data = loadData.call(this, p$globalLocale.value());
      } else {
        this.p$data = (localeManager.p$globalLocale()
          .then(globalLocale => loadData.call(this, globalLocale)) as unknown) as II18nDataPromise;
      }
    } else {
      this.setLocale(locale);
    }
  }

  /**
   * Set locale used for this model.
   * @param {string} locale
   * @returns {Promise<I18nModel>}
   */
  setLocale(locale: string): Promise<I18nModel> {
    this.p$data = loadData.call(this, locale);
    this.parserFormatterCache = {};

    return this.ready();
  }

  /**
   * Get a promise that resolves to this object when the resource is ready.
   * @return {Promise<I18nModel>}
   */
  ready(): Promise<I18nModel> {
    return this.p$data.return(this);
  }

  /*
   * Check whether the resource is ready.
   * @return {boolean}
   */
  isReady(): boolean {
    return this.p$data.isFulfilled();
  }

  /**
   * Synchronously get locale used for this model.
   * @returns {string}
   */
  getLocale(): string {
    assertReady.call(this);
    return this.p$data.value().locale;
  }

  /**
   * Synchronously get Cldrjs instance.
   * @return {any} The Cldrjs instance.
   */
  getCldr(): any {
    return synchronousGlobalize.call(this).cldr;
  }

  /**
   * Synchronously get first day of week.
   * @param {Object} [options]
   * @param {String} [options.alt]
   * @return {string}
   */
  getFirstDayOfWeek({ alt } : { alt?: string } = {}): string {
    const cldr = this.getCldr();

    const defaultRegionKey = cldr.attributes.region;
    const key = alt ? `${defaultRegionKey}-alt-${alt}` : defaultRegionKey;

    return cldr.supplemental(`weekData/firstDay/${key}`) ||
      cldr.supplemental(`weekData/firstDay/${defaultRegionKey}`) ||
      cldr.supplemental('weekData/firstDay/001');
  }

  /**
   * Synchronously get today as CivilDate.
   * @param {Object} [options]
   * @param {String} [options.timeZone]
   * @return {ICivilDate}
   */
  getToday({ timeZone = this.timeZone } : { timeZone?: string } = {}): ICivilDate {
    const [
      { value: year },
      { value: month },
      { value: day },
    ] = this.formatDateToParts(new Date(), {
      raw: 'yyyyMMdd',
      timeZone,
    });

    return {
      year: Number(year),
      month: Number(month),
      day: Number(day),
      toString() { return `${year}-${month}-${day}`; },
    };
  }

  /**
   * Synchronously get today with timing.
   * @param {Object} [options]
   * @param {String} [options.timeZone]
   * @return {ICivilDateTime}
   */
  getTodayTiming({ timeZone = this.timeZone } : { timeZone?: string } = {}): ICivilDateTime {
    const [
      { value: year },
      { value: month },
      { value: day },
      { value: hour },
      { value: minute },
      { value: second },
    ] = this.formatDateToParts(new Date(), {
      raw: 'yyyyMMddHHmmss',
      timeZone,
    });

    return {
      year: Number(year),
      month: Number(month),
      day: Number(day),
      hour: Number(hour),
      minute: Number(minute),
      second: Number(second),
      toString() { return `${year}-${month}-${day} ${hour}:${minute}:${second}`; },
    };
  }

  /**
   * Synchronously get and evaluate a localized string template.
   * @param {string} key
   * @param {...any} args
   * @return {string}
   */
  getString(key: string, ...args: any[]): string {
    let str = synchronousResource.call(this, key).getString(key, ...args);
    if (_.isUndefined(str)) {
      this.missingKeys[key] = true;
      if (this.failFast) {
        try {
          throw new RangeError(`i18n.getString: key is missing _TL_ fixture - "${key}"`);
        } catch (error) {
          if (this.failFast !== 'SAFE') {
            throw error;
          } else {
            _.defer(() => { throw error; });
            str = key;
          }
        }
      }
    }
    return str;
  }

  /**
   * Synchronously get all the localized string data.
   * @return {Object}
   */
  getData(): { [key: string]: string } {
    return {
      // @ts-ignore
      ...synchronousResource.call(this, 'getData').data,
    };
  }

  /**
   * Synchronously get quarter's translation.
   * @param {Number} quarter
   * @param {Object} [options]
   * @param {String} [options.style=wide]
   * @return {string}
   */
  getQuarter(quarter: number, { style = 'wide' } : { style?: string } = {}): string {
    return this.getCldr().main(`dates/calendars/gregorian/quarters/stand-alone/${style}/${quarter}`);
  }

  /**
   * Synchronously get month's translation.
   * @param {Number} month
   * @param {Object} [options]
   * @param {String} [options.style=wide]
   * @return {string}
   */
  getMonth(month: number, { style = 'wide' } : { style?: string } = {}): string {
    return this.getCldr().main(`dates/calendars/gregorian/months/stand-alone/${style}/${month}`);
  }

  /**
   * Synchronously get day's translation.
   * @param {string} day
   * @param {Object} [options]
   * @param {String} [options.style=wide]
   * @return {string}
   */
  getDay(day: string, { style = 'wide' } : { style?: string } = {}): string {
    return this.getCldr().main(`dates/calendars/gregorian/days/stand-alone/${style}/${day}`);
  }

  /**
   * Alias of getDay
   * @param {string} dayOfWeek
   * @param {Object} [options]
   * @return {string}
   */
  getDayOfWeek(dayOfWeek: string, options?: { style?: string }): string {
    return this.getDay(dayOfWeek, options || {});
  }

  /**
   * Synchronously get currency's display name.
   * @param {string} [currency=this.currency]
   * @return {string}
   */
  getCurrency(currency: string = this.currency): string {
    return this.getCldr().main(`numbers/currencies/${currency}/displayName`);
  }

  /**
   * Synchronously get currency's symbol.
   * @param {string} [currency=this.currency]
   * @param {Object} [options]
   * @param {String} [options.alt]
   * @return {string}
   */
  getCurrencySymbol(currency: string = this.currency, { alt } : { alt?: string } = {}): string {
    const defaultSymbolKey = 'symbol';
    const key = alt ? `symbol-alt-${alt}` : defaultSymbolKey;

    return this.getCldr().main(`numbers/currencies/${currency}/${key}`) ||
      this.getCldr().main(`numbers/currencies/${currency}/${defaultSymbolKey}`);
  }

  /**
   * Synchronously get date format patterns for user's culture
   * @return {IDateFormats}
   */
  getDateFormats(): IDateFormats {
    const calendar = this.getCldr().main('dates/calendars/gregorian');
    const defaultProperties = ['full', 'long', 'medium', 'short'];

    return {
      default: calendar.dateTimeFormats.availableFormats.yMd,
      date: _.pick(calendar.dateFormats, defaultProperties),
      dateTime: _.pick(calendar.dateTimeFormats, defaultProperties),
      time: _.pick(calendar.timeFormats, defaultProperties),
    };
  }

  /**
   * Synchronously format decimal number with given options.
   * @param {number} number
   * @param {Object} [options]
   * @return {string}
   */
  formatDecimal(number: number, options?: Record<string, any>): string {
    return this.decimalFormatter(options)(number);
  }

  /**
   * Return a function synchronously format decimal number with given options.
   * @param {Object} [options]
   * @return {(value: number) => string}
   */
  decimalFormatter({
    minimumIntegerDigits,
    minimumFractionDigits,
    maximumFractionDigits,
    minimumSignificantDigits,
    maximumSignificantDigits,
    round,
    useGrouping,
    compact,
    withSign = 'auto',
  } : Record<string, any> = {}): (value: number) => string {
    return numberFormatterWithSign.call(this, withSign, {
      style: 'decimal',
      minimumIntegerDigits,
      minimumFractionDigits,
      maximumFractionDigits,
      minimumSignificantDigits,
      maximumSignificantDigits,
      round,
      useGrouping,
      compact,
    });
  }

  /**
   * Synchronously format decimal number to fixed-point.
   * @param {number} number
   * @param {Object} [options]
   * @return {string}
   */
  formatDecimalToFixed(number: number, options?: Record<string, any>): string {
    return this.decimalToFixedFormatter(options)(number);
  }

  /**
   * Return a function synchronously format decimal number to fixed-point.
   * @param {Object} [options]
   * @return {(value: number) => string}
   */
  decimalToFixedFormatter({
    digits = 2,
    round,
    useGrouping,
    compact,
    withSign = 'auto',
  } : Record<string, any> = {}): (value: number) => string {
    return this.decimalFormatter({
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
      round,
      useGrouping,
      compact,
      withSign,
    });
  }

  /**
   * Synchronously format decimal number to integer.
   * @param {number} number
   * @param {Object} [options]
   * @return {string}
   */
  formatDecimalToInteger(number: number, options?: Record<string, any>): string {
    return this.decimalToIntegerFormatter(options)(number);
  }

  /**
   * Return a function synchronously format decimal number to integer.
   * @param {Object} [options]
   * @return {(value: number) => string}
   */
  decimalToIntegerFormatter({
    round,
    useGrouping,
    compact,
    withSign = 'auto',
  } : Record<string, any> = {}): (value: number) => string {
    return this.decimalToFixedFormatter({
      digits: 0,
      round,
      useGrouping,
      compact,
      withSign,
    });
  }

  /**
   * Synchronously parse decimal number.
   * @param {string} value
   * @return {number}
   */
  parseDecimal(value: string): number {
    return this.decimalParser()(value);
  }

  /**
   * Return a function synchronously parse decimal number.
   * @return {(value: string) => number}
   */
  decimalParser(): (value: string) => number {
    return synchronousGlobalize.call(this).numberParser({
      style: 'decimal',
    });
  }

  /**
   * Synchronously validate decimal number.
   * @param {string} value
   * @return {boolean}
   */
  isValidDecimal(value: string): boolean {
    return this.decimalValidator()(value);
  }

  /**
   * Return a function synchronously validate decimal number.
   * @return {(value: string) => boolean}
   */
  decimalValidator(): (value: string) => boolean {
    const parser = this.decimalParser();
    return value => _.isString(value) && !_.isNaN(parser(value));
  }

  /**
   * Synchronously format percent number with given options.
   * @param {number} number
   * @param {Object} [options]
   * @return {string}
   */
  formatPercent(number: number, options?: Record<string, any>): string {
    return this.percentFormatter(options)(number);
  }

  /**
   * Return a function synchronously format percent number with given options.
   * @param {Object} [options]
   * @return {(value: number) => string}
   */
  percentFormatter({
    minimumIntegerDigits,
    minimumFractionDigits,
    maximumFractionDigits,
    minimumSignificantDigits,
    maximumSignificantDigits,
    round,
    useGrouping,
    compact,
    withSign = 'auto',
  } : Record<string, any> = {}): (value: number) => string {
    return numberFormatterWithSign.call(this, withSign, {
      style: 'percent',
      minimumIntegerDigits,
      minimumFractionDigits,
      maximumFractionDigits,
      minimumSignificantDigits,
      maximumSignificantDigits,
      round,
      useGrouping,
      compact,
    });
  }

  /**
   * Synchronously format percent number to fixed-point.
   * @param {number} number
   * @param {Object} [options]
   * @return {string}
   */
  formatPercentToFixed(number: number, options?: Record<string, any>): string {
    return this.percentToFixedFormatter(options)(number);
  }

  /**
   * Return a function synchronously format percent number to fixed-point.
   * @param {Object} [options]
   * @return {(value: number) => string}
   */
  percentToFixedFormatter({
    digits = 0,
    round,
    useGrouping,
    compact,
    withSign = 'auto',
  } : Record<string, any> = {}): (value: number) => string {
    return this.percentFormatter({
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
      round,
      useGrouping,
      compact,
      withSign,
    });
  }

  /**
   * Synchronously parse percent number.
   * @param {string} value
   * @return {number}
   */
  parsePercent(value: string): number {
    return this.percentParser()(value);
  }

  /**
   * Return a function synchronously parse percent number.
   * @return {(value: string) => number}
   */
  percentParser(): (value: string) => number {
    return synchronousGlobalize.call(this).numberParser({
      style: 'percent',
    });
  }

  /**
   * Synchronously validate percent number.
   * @param {string} value
   * @return {boolean}
   */
  isValidPercent(value: string): boolean {
    return this.percentValidator()(value);
  }

  /**
   * Return a function synchronously validate percent number.
   * @return {(value: string) => boolean}
   */
  percentValidator(): (value: string) => boolean {
    const parser = this.percentParser();
    return value => _.isString(value) && !_.isNaN(parser(value));
  }

  /**
   * Synchronously format currency with given options.
   * @param {number} number
   * @param {string} currency
   * @param {Object} [options]
   * @return {string}
   */
  formatCurrency(number: number, currency: string = this.currency, options?: Record<string, any>): string {
    return this.currencyFormatter(currency, options)(number);
  }

  /**
   * Return a function synchronously format currency with given options.
   * @param {string} currency
   * @param {Object} [options]
   * @return {(value: number) => string}
   */
  currencyFormatter(currency: string = this.currency, options: Record<string, any> = {}): (value: number) => string {
    const { withSign, withSymbol } = options;
    return currencyFormatterWithSymbol.call(this, currency, withSign, withSymbol, _.omit(options, 'withSign', 'withSymbol'));
  }

  /**
   * Synchronously format date with given options.
   * @param {Date} value
   * @param {Object} [options]
   * @return {string}
   */
  formatDate(value: Date, options?: IDateOptions): string {
    return this.dateFormatter(options)(value);
  }

  /**
   * Return a function synchronously format date with given options.
   * @param {Object} [options]
   * @return {(value: Date) => string}
   */
  dateFormatter(options?: IDateOptions): (value: Date) => string {
    return synchronousGlobalize.call(this)
      .dateFormatter(dateMethodOptions.call(this, options) as DateFormatterOptions);
  }

  /**
   * Synchronously format date into parts.
   * @param {Date} value
   * @param {Object} [options]
   * @return {any[]}
   */
  formatDateToParts(value: Date, options?: IDateOptions): any[] {
    return this.dateToPartsFormatter(options)(value);
  }

  /**
   * Return a function synchronously format date into parts.
   * @param {Object} [options]
   * @return {(value: Date) => any[]}
   */
  dateToPartsFormatter(options?: IDateOptions): (value: Date) => any[] {
    return synchronousGlobalize.call(this)
      .dateToPartsFormatter(dateMethodOptions.call(this, options) as DateFormatterOptions);
  }

  /**
   * Synchronously parse date.
   * @param {string} value
   * @param {Object} [options]
   * @return {string|null}
   */
  parseDate(value: string, options?: IDateOptions): string | null {
    // @ts-ignore
    return _.isString(value) ? this.dateParser(options)(value) : null;
  }

  /**
   * Return a function synchronously parse date.
   * @param {Object} [options]
   * @return {(value: string) => string|null}
   */
  dateParser(options?: IDateOptions): (value: string) => Date | null {
    return synchronousGlobalize.call(this)
      .dateParser(dateMethodOptions.call(this, options) as DateFormatterOptions);
  }

  /**
   * Synchronously validate date.
   * @param {string} value
   * @param {Object} [options]
   * @return {boolean}
   */
  isValidDate(value: string, options?: IDateOptions): boolean {
    return this.dateValidator(options)(value);
  }

  /**
   * Return a function synchronously validate date.
   * @param {Object} [options]
   * @return {(value: string) => boolean}
   */
  dateValidator(options?: IDateOptions): (value: string) => boolean {
    const parser = this.dateParser(options);
    return value => _.isString(value) && !_.isNull(parser(value));
  }

  /**
   * Synchronously format civil date to localized string.
   * @param {ICivilDate} civilDate
   * @param {Object} [options]
   * @return {string}
   */
  formatCivilDate(civilDate: ICivilDate, options?: IDateOptions): string {
    return this.civilDateFormatter(options)(civilDate);
  }

  /**
   * Return a function synchronously format civil date.
   * @param {Object} [options]
   * @return {(civilDate: ICivilDate) => string}
   */
  civilDateFormatter(options?: IDateOptions): (civilDate: ICivilDate) => string {
    const dateFormatter = this.dateFormatter(_.defaults({
      timeZone: null,
    }, _.omit(options || {}, 'datetime', 'time')));
    return civilDate => dateFormatter(new Date(civilDate.year, civilDate.month - 1, civilDate.day));
  }

  /**
   * Synchronously parse civil date.
   * @param {string} value
   * @param {Object} [options]
   * @return {string|null}
   */
  parseCivilDate(value: string, options?: IDateOptions): ICivilDate | null {
    return this.civilDateParser(options)(value);
  }

  /**
   * Return a function synchronously parse civil date.
   * @param {Object} [options]
   * @return {(value: string) => ICivilDate|null}
   */
  civilDateParser(options?: IDateOptions): (value: string) => ICivilDate | null {
    const dateParser = this.dateParser(_.defaults({
      timeZone: 'UTC',
    }, _.omit(options || {}, 'datetime', 'time')));

    return (value: string) => {
      const date = dateParser(value);
      return _.isDate(date) ? {
        year: date.getUTCFullYear(),
        month: date.getUTCMonth() + 1,
        day: date.getUTCDate(),
        toString() { return date.toISOString().substr(0, 10); },
      } : null;
    };
  }

  /**
   * Synchronously format unit with given options.
   * @param {number} value
   * @param {string} unit
   * @param {Object} [options]
   * @return {string}
   */
  formatUnit(value: number, unit: string, options?: Record<string, any>): string {
    return this.unitFormatter(unit, options)(value);
  }

  /**
   * Return a function synchronously format unit with given options.
   * @param {string} unit
   * @param {Object} [options]
   * @return {(value: number) => string}
   */
  unitFormatter(unit: string, {
    form,
    numberFormatter = this.decimalFormatter() as NumberFormatterOptions,
  } : UnitFormatterOptions = {}): (value: number) => string {
    return synchronousGlobalize.call(this).unitFormatter(unit, {
      form,
      numberFormatter,
    });
  }

  /**
   * Synchronously format list with given options.
   * @param {string[]} list
   * @param {Object} [options]
   * @return {string}
   */
  formatList(list: string[], options?: { style?: string }): string {
    return this.listFormatter(options)(list);
  }

  /**
   * Return a function synchronously format list with given options.
   * @param {Object} [options]
   * @return {(list: string[]) => string}
   */
  listFormatter({
    style = 'regular',
  } : { style?: string } = {}): (list: string[]) => string {
    const [type, variant] = style.split('-');
    const cldr = this.getCldr();
    const cldrTypeName = _.compact([
      type === 'regular' ? 'standard' : type,
      variant === 'long' ? null : variant,
    ]).join('-');
    const listPattern = cldr.main(`listPatterns/listPattern-type-${cldrTypeName}`);
    const template = (pattern: string) => ([first, second]: [string, string]) => pattern.replace('{0}', first).replace('{1}', second);
    const twoTemplate = template(listPattern['2']);
    const middleTemplate = template(listPattern.middle);
    const startTemplate = template(listPattern.start);
    const endTemplate = template(listPattern.end);

    return (list: string[]) => {
      const size = _.size(list);

      if (size === 0) {
        return '';
      } else if (size === 1) {
        return list[0];
      }

      if (size === 2) {
        return twoTemplate(list as [string, string]);
      }

      const [first, second, ...rest] = list;
      const middle = _.reduce(
        _.initial(rest),
        (memo, item) => middleTemplate([memo, item]),
        second,
      );

      return startTemplate([
        first,
        endTemplate([
          middle,
          _.last(list) as string,
        ]),
      ]);
    };
  }

  // Async API

  /**
   * Asynchronously get and evaluate a localized string template.
   * @param {string} key
   * @param {...any} args
   * @return {Promise<string>}
   */
  fetchString(key: string, ...args: any[]): Promise<string> {
    return this.p$data.then(({ resource }) => {
      const str = resource.getString(key, ...args);
      if (!str) {
        this.missingKeys[key] = true;
      }
      return str;
    });
  }

  /**
   * Asynchronously get locale used for this model.
   * @return {Promise<string>}
   */
  fetchLocale(): Promise<string> {
    return this.p$data.then(data => data.locale);
  }
}
